import {
  Users,
  Wallet,
  Target,
  DollarSign,
  ShieldCheck,
  CircleAlert,
  Calendar,
  Download,
  TrendingUp,
  TrendingDown,
  FileText,
  Landmark,
  Building2,
  ChevronRight,
  ChartColumn,
  ChartPie,
  Activity,
  Bell,
  Clock,
} from "lucide-react";

const StatCard = ({ icon, title, value, change, positive, danger }: any) => (
  <div className="relative rounded-2xl border bg-card p-4 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
    <div className="mb-3 flex items-start justify-between">
      <div className="rounded-xl bg-primary/10 p-2.5 text-primary">{icon}</div>
      <span
        className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
          danger
            ? "bg-red-50 text-red-500"
            : positive
              ? "bg-green-50 text-green-600"
              : "bg-muted text-muted-foreground"
        }`}
      >
        {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
        {change}
      </span>
    </div>

    <p className="text-2xl font-bold text-secondary">{value}</p>
    <p className="mt-1 text-xs font-medium text-muted-foreground">{title}</p>
  </div>
);

const QuickLink = ({ icon, label, href, color }: any) => (
  <a href={href} className="group">
    <div className="flex items-center gap-3 rounded-2xl border bg-card p-4 transition hover:border-primary/30 hover:shadow-md">
      <div className={`rounded-xl bg-${color}-100 p-3 text-${color}-600`}>
        {icon}
      </div>
      <p className="flex-1 text-sm font-semibold text-secondary">{label}</p>
      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
    </div>
  </a>
);

const Card = ({ title, icon, children, action }: any) => (
  <div className="rounded-2xl border bg-card shadow-sm transition hover:shadow-md">
    <div className="flex items-center justify-between p-6 pb-2">
      <h3 className="flex items-center gap-2 text-lg font-bold">
        <span className="text-primary">{icon}</span>
        {title}
      </h3>
      {action && (
        <button className="text-xs font-semibold text-muted-foreground hover:text-primary">
          {action}
        </button>
      )}
    </div>
    <div className="p-6 pt-0">{children}</div>
  </div>
);

// const ProductRow = ({ name, value }: any) => (
//   <li className="flex items-center justify-between rounded-lg bg-muted/30 p-3">
//     <span className="font-semibold">{name}</span>
//     <span className="font-bold">{value}</span>
//   </li>
// );

const Th = ({ children, right }: any) => (
  <th
    className={`p-4 text-left text-sm font-medium text-secondary ${
      right ? "text-right" : ""
    }`}
  >
    {children}
  </th>
);

const ActivityRow = ({ activity, user, product, amount, status }: any) => (
  <tr className="border-b transition hover:bg-muted/20">
    <td className="p-4 font-medium">{activity}</td>
    <td className="p-4 text-muted-foreground">{user}</td>
    <td className="p-4">{product}</td>
    <td className="p-4 text-right font-semibold">{amount}</td>
    <td className="p-4 capitalize">{status}</td>
    <td className="flex items-center gap-1 p-4 text-muted-foreground">
      <Clock className="h-3 w-3" /> just now
    </td>
  </tr>
);

const products = [
  {
    rank: 1,
    name: "REC Limited Bond",
    meta: "Bond • 234 investors",
    amount: "₹23.4 Cr",
    growth: "+12%",
  },
  {
    rank: 2,
    name: "HDFC 1-Year FD",
    meta: "FD • 189 investors",
    amount: "₹18.9 Cr",
    growth: "+8%",
  },
  {
    rank: 3,
    name: "Swiggy IPO",
    meta: "IPO • 156 investors",
    amount: "₹15.6 Cr",
    growth: "+45%",
  },
  {
    rank: 4,
    name: "ICICI Prudential NPS",
    meta: "NPS • 98 investors",
    amount: "₹4.9 Cr",
    growth: "+15%",
  },
];

const DashboardPage = () => {
  return (
    <main className="flex-1 overflow-y-auto p-8">
      <div className="space-y-6">
        {/* ================= HERO ================= */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/30 p-6 md:p-8">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(168_72%_40%/0.2)_0%,transparent_60%)]" />
          <div className="absolute bottom-0 left-0 h-64 w-64 -translate-x-1/2 translate-y-1/2 rounded-full bg-primary/10 blur-3xl" />

          <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div className="text-white">
              <p className="mb-1 text-sm font-medium text-white/60">
                Admin Dashboard
              </p>
              <h1 className="text-2xl font-bold md:text-3xl">
                Welcome back, Admin
              </h1>
              <p className="mt-1 text-white/70">
                Here&apos;s what&apos;s happening with your platform today
              </p>
            </div>

            <div className="flex gap-3">
              <button className="flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/20">
                <Calendar className="h-4 w-4" />
                Last 30 Days
              </button>

              <button className="flex items-center gap-2 rounded-xl bg-white px-5 py-2 text-sm font-semibold text-secondary shadow-sm transition hover:bg-white/90">
                <Download className="h-4 w-4" />
                Export Report
              </button>
            </div>
          </div>
        </div>

        {/* ================= KPI CARDS ================= */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <StatCard
            icon={<Users />}
            title="Total Users"
            value="12,845"
            change="+12.5%"
            positive
          />
          <StatCard
            icon={<Wallet />}
            title="Total AUM"
            value="₹48.5 Cr"
            change="+23.1%"
            positive
          />
          <StatCard
            icon={<Target />}
            title="Active Investments"
            value="8,234"
            change="+8.2%"
            positive
          />
          <StatCard
            icon={<DollarSign />}
            title="Revenue MTD"
            value="₹12.4 L"
            change="+18.6%"
            positive
          />
          <StatCard
            icon={<ShieldCheck />}
            title="KYC Pending"
            value="156"
            change="-24%"
          />
          <StatCard
            icon={<CircleAlert />}
            title="Support Tickets"
            value="23"
            change="+5"
            danger
          />
        </div>

        {/* ================= QUICK ACTIONS ================= */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <QuickLink
            icon={<FileText />}
            label="Add New IPO"
            href="/admin/ipo/management"
            color="amber"
          />
          <QuickLink
            icon={<Landmark />}
            label="Manage Bonds"
            href="/admin/bonds/management"
            color="primary"
          />
          <QuickLink
            icon={<Building2 />}
            label="FD Providers"
            href="/admin/fds/management"
            color="blue"
          />
          <QuickLink
            icon={<Users />}
            label="User Management"
            href="/admin/users"
            color="purple"
          />
        </div>

        {/* ================= PLACEHOLDERS FOR CHARTS ================= */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card title="Revenue by Product" icon={<ChartColumn />}>
            <div className="flex h-[280px] items-center justify-center text-muted-foreground">
              Chart Component (Recharts)
            </div>
          </Card>

          <Card title="AUM Distribution" icon={<ChartPie />}>
            <div className="flex h-[180px] items-center justify-center text-muted-foreground">
              Pie Chart Component
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card title="Weekly Activity" icon={<Activity />}>
            <div className="flex h-[220px] items-center justify-center text-muted-foreground">
              Bar Chart Component
            </div>
          </Card>

          <div className="rounded-2xl border border-border/60 bg-card text-card-foreground shadow-sm transition-shadow duration-300 hover:shadow-md">
            {/* ===== Header ===== */}
            <div className="flex flex-col space-y-1.5 p-6 pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-bold tracking-tight text-foreground">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Top Performing Products
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    By investment volume this month
                  </p>
                </div>

                <button className="h-9 rounded-lg px-4 text-xs font-semibold text-foreground transition hover:bg-muted/50">
                  View All
                </button>
              </div>
            </div>

            {/* ===== List ===== */}
            <div className="p-6 pt-0">
              <div className="space-y-4">
                {products.map(product => (
                  <div
                    key={product.rank}
                    className="flex items-center gap-4 rounded-lg bg-muted/30 p-3 transition-colors hover:bg-muted/50"
                  >
                    {/* Rank */}
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold text-primary">
                      {product.rank}
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-secondary">
                        {product.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {product.meta}
                      </p>
                    </div>

                    {/* Amount */}
                    <div className="text-right">
                      <p className="font-bold text-secondary">
                        {product.amount}
                      </p>
                      <p className="text-xs text-green-600">{product.growth}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ================= RECENT ACTIVITIES ================= */}
        <Card title="Recent Activities" icon={<Bell />} action="View All">
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr>
                <Th>Activity</Th>
                <Th>User</Th>
                <Th>Product</Th>
                <Th right>Amount</Th>
                <Th>Status</Th>
                <Th>Time</Th>
              </tr>
            </thead>
            <tbody>
              <ActivityRow
                activity="New IPO Application"
                user="Rahul Sharma"
                product="Swiggy IPO"
                amount="₹1,50,000"
                status="pending"
              />
              <ActivityRow
                activity="Bond Purchase"
                user="Priya Patel"
                product="REC Ltd Bond"
                amount="₹5,00,000"
                status="completed"
              />
            </tbody>
          </table>
        </Card>
      </div>
    </main>
  );
};

export default DashboardPage;
